import { useState, useEffect } from 'react';
import api from '../../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { toast } from 'sonner';
import { Tent, CreditCard, Zap } from 'lucide-react';

const CampBookingsPanel = () => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [charging, setCharging] = useState(null);
  const [chargeTarget, setChargeTarget] = useState(null);
  const [chargeAmount, setChargeAmount] = useState('');

  const fetchRows = async () => {
    try {
      const res = await api.get('/bootcamps/bookings/all');
      const unpaid = (res.data || []).filter(
        (b) =>
          ['pending', 'partial'].includes(b.payment_status) &&
          !['cancelled', 'refunded'].includes(b.status) &&
          !b.is_comp
      );
      setRows(unpaid);
    } catch (e) {
      console.error('Failed to fetch camp bookings:', e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchRows(); }, []);

  const remaining = (b) => Math.max(0, Number(b.amount || 0) - Number(b.amount_paid || 0));

  const handleCharge = async () => {
    const b = chargeTarget;
    const amt = parseFloat(chargeAmount);
    if (!b) return;
    if (!amt || amt <= 0) {
      toast.error('Enter a valid amount');
      return;
    }
    setCharging(b.id);
    try {
      const res = await api.post(`/bootcamps/bookings/${b.id}/charge-saved-card`, {
        amount: Number(b.amount_paid || 0) + amt,
      });
      toast.success(`Charged £${(res.data.amount_charged ?? amt).toFixed(2)} — ${b.student_name}'s place secured`);
      setChargeTarget(null);
      fetchRows();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Charge failed');
    } finally {
      setCharging(null);
    }
  };

  if (!loading && rows.length === 0) return null;

  return (
    <Card className="bg-[#111] border-[#333]" data-testid="camp-bookings-panel">
      <CardHeader>
        <CardTitle className="text-white flex items-center gap-2 text-base">
          <Tent className="w-5 h-5 text-purple-400" />
          Camp Bookings — Awaiting Payment
          <span className="text-sm text-white/50 font-normal">({rows.length})</span>
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-2">
        {loading ? (
          <p className="text-white/50 text-sm">Loading...</p>
        ) : (
          rows.map((b) => (
            <div
              key={b.id}
              className="flex flex-wrap items-center justify-between gap-2 p-3 bg-[#0a0a0a] rounded-lg border border-[#222]"
              data-testid={`camp-pending-${b.id}`}
            >
              <div className="min-w-0">
                <p className="text-white font-medium">
                  {b.student_name} <span className="text-white/50 font-normal">— {b.bootcamp_name}</span>
                </p>
                <p className="text-xs text-white/50 truncate">
                  {b.parent_name || ''}{b.parent_email ? ` · ${b.parent_email}` : ''}
                </p>
                <div className="flex gap-2 mt-1 flex-wrap">
                  {b.payment_status === 'partial' && (
                    <Badge className="bg-amber-500/20 text-amber-300 border-amber-500/30 text-xs">
                      Partial — £{Number(b.amount_paid || 0).toFixed(2)} paid
                    </Badge>
                  )}
                  {b.card_last4 ? (
                    <Badge className="bg-emerald-500/20 text-emerald-300 border-emerald-500/30 text-xs" data-testid={`card-badge-${b.id}`}>
                      <CreditCard className="w-3 h-3 mr-1" />
                      {(b.card_brand || 'Card').toUpperCase()} •••• {b.card_last4}
                    </Badge>
                  ) : (
                    <Badge className="bg-neutral-500/20 text-neutral-300 border-neutral-500/30 text-xs">
                      No card on file
                    </Badge>
                  )}
                </div>
              </div>
              <div className="flex items-center gap-3">
                <span className="text-white font-bold">£{remaining(b).toFixed(2)}</span>
                <Button
                  size="sm"
                  onClick={() => { setChargeTarget(b); setChargeAmount(remaining(b).toFixed(2)); }}
                  disabled={charging === b.id}
                  className="bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600"
                  data-testid={`charge-camp-${b.id}`}
                >
                  <Zap className="w-4 h-4 mr-1" />
                  {charging === b.id ? 'Charging…' : `Charge £${remaining(b).toFixed(2)}`}
                </Button>
              </div>
            </div>
          ))
        )}
      </CardContent>

      {/* Charge dialog — supports pro-rated amounts for partial days */}
      <Dialog open={!!chargeTarget} onOpenChange={(v) => { if (!v) setChargeTarget(null); }}>
        <DialogContent className="bg-[#111] border-[#333] text-white max-w-md">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <Zap className="w-5 h-5 text-purple-400" />
              Charge saved card
            </DialogTitle>
          </DialogHeader>
          {chargeTarget && (
            <div className="space-y-3">
              <p className="text-sm text-white/70">
                <strong>{chargeTarget.student_name}</strong> — {chargeTarget.bootcamp_name}
                {chargeTarget.card_last4 && (
                  <span className="ml-2 text-emerald-300">
                    {(chargeTarget.card_brand || 'card').toUpperCase()} •••• {chargeTarget.card_last4}
                  </span>
                )}
              </p>
              <div>
                <label className="text-xs text-white/50 block mb-1">Amount to charge (£)</label>
                <Input
                  type="number"
                  step="0.01"
                  min="0"
                  value={chargeAmount}
                  onChange={(e) => setChargeAmount(e.target.value)}
                  className="bg-[#1a1a1a] border-[#333] text-white"
                  data-testid="charge-amount-input"
                />
                <p className="text-xs text-white/40 mt-1">
                  Full remaining: £{remaining(chargeTarget).toFixed(2)} — enter a lower amount to pro-rate for
                  partial days. This option isn't shown to parents.
                </p>
              </div>
            </div>
          )}
          <DialogFooter className="gap-2">
            <Button
              variant="outline"
              onClick={() => setChargeTarget(null)}
              className="border-[#333] text-white hover:bg-[#222]"
              data-testid="charge-cancel-btn"
            >
              Cancel
            </Button>
            <Button
              onClick={handleCharge}
              disabled={charging === chargeTarget?.id}
              className="bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600"
              data-testid="charge-confirm-btn"
            >
              {charging === chargeTarget?.id
                ? 'Charging…'
                : `Charge £${(parseFloat(chargeAmount) || 0).toFixed(2)}`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
};

export default CampBookingsPanel;
